import React, { useEffect, useMemo, useState } from 'react';
import { EarlyAccessService } from '@/services/early-access.service';
import {
  EarlyAccessApplication,
  EarlyAccessConfig,
  EarlyAccessStatus,
} from '@/models/early-access.model';
import { ShieldCheck, Users, CheckCircle2, Clock } from 'lucide-react';

const statusOptions: { label: string; value: EarlyAccessStatus | 'all' }[] = [
  { label: 'All', value: 'all' },
  { label: 'Applied', value: 'applied' },
  { label: 'Shortlisted', value: 'shortlisted' },
  { label: 'Accepted', value: 'accepted' },
  { label: 'Rejected', value: 'rejected' },
  { label: 'Onboarded', value: 'onboarded' },
];

const sizeOptions = ['', '1-10', '11-50', '51-200', '200+'];
const timelineOptions = ['', 'Immediate', 'Next 30 days', 'This quarter', 'Exploring'];

export const EarlyAccessAdmin: React.FC = () => {
  const [applications, setApplications] = useState<EarlyAccessApplication[]>([]);
  const [config, setConfig] = useState<EarlyAccessConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<EarlyAccessApplication | null>(null);
  const [notes, setNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all' as EarlyAccessStatus | 'all',
    companySize: '',
    timeline: '',
    createdFrom: '',
    createdTo: '',
  });

  const refreshData = async () => {
    setLoading(true);
    try {
      const [apps, cfg] = await Promise.all([
        EarlyAccessService.getApplications({
          status: filters.status,
          companySize: filters.companySize || undefined,
          timeline: filters.timeline || undefined,
          createdFrom: filters.createdFrom ? new Date(filters.createdFrom) : null,
          createdTo: filters.createdTo ? new Date(filters.createdTo) : null,
        }),
        EarlyAccessService.getConfig(),
      ]);
      setApplications(apps);
      setConfig(cfg);
      if (selected) {
        const updated = apps.find((app) => app.id === selected.id) || null;
        setSelected(updated);
        setNotes(updated?.notes || '');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshData();
  }, [filters.status, filters.companySize, filters.timeline, filters.createdFrom, filters.createdTo]);

  const stats = useMemo(() => {
    const counts = {
      total: applications.length,
      applied: applications.filter((app) => app.status === 'applied').length,
      shortlisted: applications.filter((app) => app.status === 'shortlisted').length,
      accepted: applications.filter((app) => app.status === 'accepted').length,
      rejected: applications.filter((app) => app.status === 'rejected').length,
      onboarded: applications.filter((app) => app.status === 'onboarded').length,
    };
    const capacity = config?.capacity || 25;
    const acceptedCount = config?.acceptedCount ?? counts.accepted + counts.onboarded;
    const remaining = Math.max(capacity - acceptedCount, 0);
    return { ...counts, capacity, acceptedCount, remaining };
  }, [applications, config]);

  const handleReview = async (action: 'shortlist' | 'accept' | 'reject') => {
    if (!selected) return;
    setActionLoading(true);
    try {
      await EarlyAccessService.reviewApplication({
        applicationId: selected.id,
        action,
        notes: notes || null,
      });
      await refreshData();
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-gradient-to-br from-blue-600 to-teal-500 rounded-lg">
            <ShieldCheck className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Founding Employer Pilot</h1>
            <p className="text-gray-600">Review applications, manage statuses, and track scarcity.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg p-4 border border-gray-100">
            <div className="text-sm text-gray-500">Capacity</div>
            <div className="text-2xl font-semibold text-gray-900">{stats.acceptedCount} / {stats.capacity}</div>
            <div className="text-xs text-gray-500">{stats.remaining} spots left</div>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-100">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Clock className="w-4 h-4 text-blue-600" /> Applied
            </div>
            <div className="text-2xl font-semibold text-gray-900">{stats.applied}</div>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-100">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Users className="w-4 h-4 text-amber-500" /> Shortlisted
            </div>
            <div className="text-2xl font-semibold text-gray-900">{stats.shortlisted}</div>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-100">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <CheckCircle2 className="w-4 h-4 text-green-600" /> Accepted
            </div>
            <div className="text-2xl font-semibold text-gray-900">{stats.accepted}</div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="text-xs text-gray-500">Status</label>
              <select
                className="mt-1 w-full border border-gray-200 rounded-lg p-2 text-sm"
                value={filters.status}
                onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value as any }))}
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500">Company size</label>
              <select
                className="mt-1 w-full border border-gray-200 rounded-lg p-2 text-sm"
                value={filters.companySize}
                onChange={(e) => setFilters((prev) => ({ ...prev, companySize: e.target.value }))}
              >
                {sizeOptions.map((size) => (
                  <option key={size || 'all'} value={size}>{size || 'All'}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500">Timeline</label>
              <select
                className="mt-1 w-full border border-gray-200 rounded-lg p-2 text-sm"
                value={filters.timeline}
                onChange={(e) => setFilters((prev) => ({ ...prev, timeline: e.target.value }))}
              >
                {timelineOptions.map((timeline) => (
                  <option key={timeline || 'all'} value={timeline}>{timeline || 'All'}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500">Created from</label>
              <input
                type="date"
                className="mt-1 w-full border border-gray-200 rounded-lg p-2 text-sm"
                value={filters.createdFrom}
                onChange={(e) => setFilters((prev) => ({ ...prev, createdFrom: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-xs text-gray-500">Created to</label>
              <input
                type="date"
                className="mt-1 w-full border border-gray-200 rounded-lg p-2 text-sm"
                value={filters.createdTo}
                onChange={(e) => setFilters((prev) => ({ ...prev, createdTo: e.target.value }))}
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Applications</h2>
              <span className="text-sm text-gray-500">{stats.total} total</span>
            </div>
            {loading ? (
              <div className="py-10 text-center text-gray-500">Loading applications...</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="text-xs text-gray-500 uppercase border-b">
                    <tr>
                      <th className="text-left py-2">Company</th>
                      <th className="text-left py-2">Size</th>
                      <th className="text-left py-2">Timeline</th>
                      <th className="text-left py-2">Status</th>
                      <th className="text-left py-2">Referrals</th>
                      <th className="text-left py-2">Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {applications.map((app) => (
                      <tr
                        key={app.id}
                        className={`border-b hover:bg-gray-50 cursor-pointer ${selected?.id === app.id ? 'bg-blue-50' : ''}`}
                        onClick={() => {
                          setSelected(app);
                          setNotes(app.notes || '');
                        }}
                      >
                        <td className="py-3">
                          <div className="font-medium text-gray-900">{app.companyName}</div>
                          <div className="text-xs text-gray-500">{app.email}</div>
                        </td>
                        <td className="py-3 text-gray-700">{app.companySize}</td>
                        <td className="py-3 text-gray-700">{app.timeline}</td>
                        <td className="py-3 text-gray-700 capitalize">{app.status}</td>
                        <td className="py-3 text-gray-700">
                          {app.referralCount} ({app.qualifiedReferralCount} q)
                        </td>
                        <td className="py-3 text-gray-700">{app.score}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Application detail</h2>
              {selected && (
                <span className="text-xs text-gray-500">Submitted {selected.createdAt.toLocaleDateString()}</span>
              )}
            </div>
            {!selected ? (
              <div className="py-10 text-center text-gray-500">Select an application to review.</div>
            ) : (
              <div className="space-y-4 text-sm text-gray-700">
                <div>
                  <div className="text-xs uppercase text-gray-400">Company</div>
                  <div className="font-medium text-gray-900">{selected.companyName}</div>
                  <div className="text-xs text-gray-500">{selected.website}</div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs uppercase text-gray-400">Role</div>
                    <div>{selected.applicantRole}</div>
                  </div>
                  <div>
                    <div className="text-xs uppercase text-gray-400">Benefits setup</div>
                    <div>{selected.benefitsSetup}</div>
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase text-gray-400">Pain points</div>
                  <div>{selected.painPoints.join(', ') || selected.painPointOther || 'None'}</div>
                </div>
                <div>
                  <div className="text-xs uppercase text-gray-400">Interest note</div>
                  <div>{selected.interestReason || 'Not provided'}</div>
                </div>
                <div>
                  <div className="text-xs uppercase text-gray-400">Referrer</div>
                  <div>{selected.referrerCompanyName || selected.referrerCode || 'Direct'}</div>
                </div>

                <div>
                  <label className="text-xs uppercase text-gray-400">Notes</label>
                  <textarea
                    className="mt-1 w-full border border-gray-200 rounded-lg p-2 text-sm"
                    rows={3}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </div>

                <div className="flex flex-wrap gap-2 pt-2">
                  <button
                    className="px-4 py-2 rounded-lg text-sm font-medium bg-amber-50 text-amber-700 border border-amber-200"
                    disabled={actionLoading}
                    onClick={() => handleReview('shortlist')}
                  >
                    Shortlist
                  </button>
                  <button
                    className="px-4 py-2 rounded-lg text-sm font-medium bg-green-50 text-green-700 border border-green-200"
                    disabled={actionLoading}
                    onClick={() => handleReview('accept')}
                  >
                    Accept
                  </button>
                  <button
                    className="px-4 py-2 rounded-lg text-sm font-medium bg-red-50 text-red-700 border border-red-200"
                    disabled={actionLoading}
                    onClick={() => handleReview('reject')}
                  >
                    Reject
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
